import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { BackgroundProcessService, ClientEnvironment, ClientNotificationHandlerService, ENVIRONMENT } from '@thehubz/client/core';
import {
  Asset,
  AssetHistory,
  AssetHistoryApiService,
  AssetHistoryResultResponse,
  ExportExcelRequest,
  ExportsApiService,
} from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { tap } from 'rxjs';

@Component({
  selector: 'thehubz-bulk-export-downloads',
  templateUrl: './bulk-export-downloads.component.html',
  styleUrls: ['./bulk-export-downloads.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatFormFieldModule, MatCheckboxModule, MatButtonModule, MatDialogModule],
})
export class BulkExportDownloadsComponent {
  exportDownloadsForm: UntypedFormGroup;
  downloadDataFlag = false;
  allComplete = true;
  downloadData: AssetHistory[] = [];
  httpCallInProgress = false;
  brandingKey!: CustomClients;

  constructor(
    @Inject(MAT_DIALOG_DATA) public assets: AnyType,
    @Inject(ENVIRONMENT) private environment: ClientEnvironment,
    private _formBuilder: UntypedFormBuilder,
    private assetHistoryService: AssetHistoryApiService,
    private exportExcelApiService: ExportsApiService,
    private backgroundProcessService: BackgroundProcessService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private dialogRef: MatDialogRef<BulkExportDownloadsComponent>,
  ) {
    this.exportDownloadsForm = this._formBuilder.group({
      isActive: [true],
      selected: [assets, [Validators.required]],
    });
    this.brandingKey = environment.brandingKey ?? CustomClients.Standard;
  }

  someComplete(): boolean {
    const selectedAssetFormValue = this.exportDownloadsForm.get('selected')?.value;
    if (selectedAssetFormValue === '') {
      return false;
    }
    return selectedAssetFormValue.length > 0 && !this.allComplete;
  }

  checkboxClick(assetId: AnyType) {
    const selectedAssetFormValue = this.exportDownloadsForm.get('selected')?.value;
    const selectedAssets = selectedAssetFormValue ? selectedAssetFormValue : [];
    const selectedAsset = selectedAssets?.filter((asset: { _id: AnyType }) => {
      return asset._id === assetId;
    });
    if (selectedAsset.length) {
      const filteredAssets = selectedAssets?.filter((asset: { _id: AnyType }) => {
        return asset._id !== assetId;
      });
      this.exportDownloadsForm.get('selected')?.setValue(filteredAssets);
    } else {
      const newAsset = this.assets.filter((asset: { _id: AnyType }) => {
        return asset._id === assetId;
      });
      selectedAssets.push(...newAsset);
      this.exportDownloadsForm.get('selected')?.setValue(selectedAssets);
    }
    this.allComplete = this.exportDownloadsForm.get('selected')?.value.length === this.assets.length;
  }

  checkAll(completed: boolean) {
    this.allComplete = completed;
    if (this.allComplete) {
      this.exportDownloadsForm.get('selected')?.setValue(this.assets);
    } else {
      this.exportDownloadsForm.get('selected')?.setValue('');
    }
  }

  getCheckedStatus(assetId: AnyType) {
    const selectedAssets = this.exportDownloadsForm.get('selected')?.value;
    if (selectedAssets) {
      const selectedAsset = selectedAssets?.filter((asset: { _id: AnyType }) => {
        return asset._id === assetId;
      });
      if (selectedAsset.length) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  onSubmit() {
    const assetIDs = this.exportDownloadsForm.get('selected')?.value.map((asset: Asset) => asset._id);
    const filter = { filters: { downloadedAsset: { $in: assetIDs } } };
    this.assetHistoryService
      .assetHistoryControllerFindAllDownloadedAssets(JSON.stringify(filter))
      .pipe(
        tap(() => {
          this._showLoader(false);
        }),
      )
      .subscribe({
        next: (response: AssetHistoryResultResponse) => {
          if (response.result && response.result.length > 0) {
            this.downloadData = response.result;
            // filter is the mongo query for mongo.find()
            // filterOptions is the options for mongo.find()
            let filterOptions = {};
            if (this.brandingKey === CustomClients.SEAT) {
              filterOptions = { agency: 0, module: 0, channel: 0 };
            }
            if (this.brandingKey === CustomClients.Standard) {
              filterOptions = { channel: 0, licenseRegion: 0, purpose: 0 };
            }
            if (this.downloadData.length) {
              const process$ = this.exportExcelApiService.exportsControllerExportExcel({
                type: ExportExcelRequest.TypeEnum.Assethistories,
                filter: JSON.stringify(filter.filters),
                fileName: this.environment.exportDownloads?.fileName,
                filterOptions: JSON.stringify(filterOptions),
                addTime: this.environment.exportDownloads?.addTime,
              });
              this.backgroundProcessService.addExportProcess(process$, ExportExcelRequest.TypeEnum.Assethistories);
              this.dialogRef.close();
            } else {
              this.dialogRef.close();
              this.clientNotificationHandlerService.errorNotification($localize`No Export History to download`);
            }
          } else {
            this.downloadDataFlag = true;
          }
        },
        error: (err) => {
          this.clientNotificationHandlerService.errorNotification(err);
        },
      });
  }

  /**
   * Utility method to show/hide loader
   */
  private _showLoader(show = true) {
    this.httpCallInProgress = show;
  }
}
